import React, { useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { useDictation } from '../hooks/useDictation';
import { cn } from '../lib/utils';

/**
 * Wraps an input or textarea with a microphone button for voice dictation.
 * Use as: <InputWithDictation as="input" ... /> or as="textarea" ...
 */
export function InputWithDictation({
  as: Component = 'input',
  value = '',
  onChange,
  onBlur,
  className,
  disabled,
  placeholder,
  ...rest
}) {
  const handleResult = useCallback(
    (transcript) => {
      if (onChange) onChange({ target: { value: transcript } });
    },
    [onChange]
  );
  const { isListening, start, supported } = useDictation({
    onResult: handleResult,
    lang: 'es-ES',
  });

  const handleMicClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!supported || disabled) return;
    start();
  };

  return (
    <div className="relative flex items-stretch rounded-md border border-input bg-background overflow-hidden">
      <Component
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={placeholder}
        className={cn(
          'flex-1 min-w-0 rounded-none border-0 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset',
          Component === 'textarea' && 'min-h-[80px] resize-y',
          className
        )}
        {...rest}
      />
      {supported && (
        <button
          type="button"
          onClick={handleMicClick}
          disabled={disabled}
          title={isListening ? 'Escuchando...' : 'Dictar'}
          className={cn(
            'flex items-center justify-center w-9 shrink-0 border-l border-input bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
            isListening && 'bg-primary/20 text-primary'
          )}
        >
          {isListening ? (
            <MicOff className="h-4 w-4 animate-pulse" />
          ) : (
            <Mic className="h-4 w-4" />
          )}
        </button>
      )}
    </div>
  );
}

export default InputWithDictation;
